import { useState } from 'react';
import { ParsedIssue } from '../../lib/api/intelligence';

interface ParsedIssuesListProps {
  issues: ParsedIssue[];
  editable?: boolean;
  onChange?: (issues: ParsedIssue[]) => void;
}

const severityConfig: Record<
  ParsedIssue['severity'],
  { badge: string; label: string; dot: string }
> = {
  rejection: {
    badge: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
    label: 'Rejection',
    dot: 'bg-red-500',
  },
  deficiency: {
    badge: 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300',
    label: 'Deficiency',
    dot: 'bg-orange-500',
  },
  revision: {
    badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300',
    label: 'Revision',
    dot: 'bg-yellow-500',
  },
  warning: {
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
    label: 'Warning',
    dot: 'bg-blue-500',
  },
};

function confidenceColor(confidence: number): string {
  if (confidence >= 0.8) return 'bg-green-500';
  if (confidence >= 0.5) return 'bg-yellow-500';
  return 'bg-red-500';
}

function confidenceDot(confidence: number): string {
  if (confidence >= 0.8) return 'bg-green-500';
  if (confidence >= 0.5) return 'bg-yellow-500';
  return 'bg-red-500';
}

interface IssueCardProps {
  issue: ParsedIssue;
  editable: boolean;
  onChange: (updated: ParsedIssue) => void;
}

function IssueCard({ issue, editable, onChange }: IssueCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(issue);
  const sev = severityConfig[issue.severity];

  const handleSave = () => {
    onChange(draft);
    setEditing(false);
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${sev.badge}`}>
            {sev.label}
          </span>
          {issue.issue_category && (
            <span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {issue.issue_category}
            </span>
          )}
          {issue.issue_subcategory && (
            <span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {issue.issue_subcategory}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={`w-2.5 h-2.5 rounded-full ${confidenceDot(issue.confidence)}`}
            title={`Confidence: ${Math.round(issue.confidence * 100)}%`}
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {Math.round(issue.confidence * 100)}%
          </span>
          {editable && (
            <button
              onClick={() => setEditing((v) => !v)}
              className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
            >
              {editing ? 'Cancel' : 'Edit'}
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Field Name
            </label>
            <input
              type="text"
              value={draft.field_name}
              onChange={(e) => setDraft((d) => ({ ...d, field_name: e.target.value }))}
              className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-800 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Expected Value
            </label>
            <input
              type="text"
              value={draft.expected_value}
              onChange={(e) => setDraft((d) => ({ ...d, expected_value: e.target.value }))}
              className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-800 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Category
            </label>
            <input
              type="text"
              value={draft.issue_category}
              onChange={(e) => setDraft((d) => ({ ...d, issue_category: e.target.value }))}
              className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-800 dark:text-white"
            />
          </div>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 rounded bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
          >
            Save
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5">
                Field
              </p>
              <p className="text-gray-800 dark:text-white/90 font-mono text-xs">
                {issue.field_name}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5">
                Submitted Value
              </p>
              <p className="text-gray-800 dark:text-white/90 font-mono text-xs">
                {issue.field_value || '—'}
              </p>
            </div>
            {issue.expected_value && (
              <div className="col-span-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5">
                  Expected Value
                </p>
                <p className="text-green-700 dark:text-green-400 font-mono text-xs">
                  {issue.expected_value}
                </p>
              </div>
            )}
          </div>

          {issue.description && (
            <p className="text-sm text-gray-700 dark:text-gray-300">{issue.description}</p>
          )}

          {issue.form_section && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Section: {issue.form_section}
            </p>
          )}

          <div className="mt-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Confidence</span>
              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${confidenceColor(issue.confidence)}`}
                  style={{ width: `${Math.round(issue.confidence * 100)}%` }}
                />
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {Math.round(issue.confidence * 100)}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ParsedIssuesList({ issues, editable = false, onChange }: ParsedIssuesListProps) {
  const handleChange = (index: number, updated: ParsedIssue) => {
    if (!onChange) return;
    const next = [...issues];
    next[index] = updated;
    onChange(next);
  };

  if (issues.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
        No parsed issues found.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {issues.map((issue, i) => (
        <IssueCard
          key={i}
          issue={issue}
          editable={editable}
          onChange={(updated) => handleChange(i, updated)}
        />
      ))}
    </div>
  );
}
